import { useContext, useCallback, useRef } from "react";
import { AuthContext } from "src/utils/AuthContext.js";
import { useStripe } from "@stripe/react-stripe-js";
import { useNavigate } from "react-router-dom";
import { useIndexedDB } from "src/utils/useIndexedDB.js";
import { getApiRoute } from "src/services/Config.js";
import axios from "axios";
import { customEvent } from "./gtag.js";
import { getUtmAttribution } from "./attribution.js";
import {
  Features,
  getUpgradeFlow,
  hasAccess,
  hasValidConsumerSubscription,
} from "src/services/FeatureGatingService.js";
import { useSubscriptionPolling } from "src/utils/useSubscriptionPolling.js";
import { rollbar } from "src/utils/ErrorBoundary/index.js";
import { backendErrorMessage, isSessionError } from "src/utils/authErrorUtils.js";

export const paymentStateDefaultValue = {
  simplePaymentFailed: false,
};

export const useSimplePayment = ({
  type = "consumer_annual",
  trialPeriodDays = 7,
  onSuccess = () => { },
}) => {
  const { user } = useContext(AuthContext);
  const stripe = useStripe();
  const navigate = useNavigate();
  const { pollForSubscription } = useSubscriptionPolling();
  const [
    paymentStates,
    setPaymentStates,
    { setSimplePaymentFailed },
    dbLoading,
  ] = useIndexedDB(
    "stanza",
    "states",
    `payment-${user?._id ?? "guest"}`,
    paymentStateDefaultValue
  );
  const { simplePaymentFailed } = paymentStates;
  const isSimplePayment = !!user?.stripeCustomerId && !simplePaymentFailed;
  const paymentInProgressRef = useRef(false);

  const shouldTrySimplePayment = useCallback(
    (featureKey) => {
      return (
        isSimplePayment &&
        !hasAccess(user, featureKey) &&
        !hasValidConsumerSubscription(user)
      );
    },
    [isSimplePayment, user]
  );

  const handleSimplePayment = useCallback(
    async ({ featureKey }) => {
      if (paymentInProgressRef.current) {
        return false;
      }
      paymentInProgressRef.current = true;

      try {
        const source = Features[featureKey].upgradeSource;
        customEvent({
          name: `handled_simple_payment_${source}`,
          category: "Upgrade",
          label: "Successful Simple Payment",
          data: {
            user_id: user._id,
            user_email: user.email,
            user_handle: user.handle ?? "no_handle",
            trial_period: trialPeriodDays > 0,
          },
        });
        // Synchronous step: hit the BE. If this errors (declined customer,
        // missing price, etc.) we fall through to the catch + navigate to
        // /upgrade/plus — that's the only error path the user actually sees.
        const attribution = getUtmAttribution();
        const response = await axios.post(
          getApiRoute("upgrade", "STRIPE_NATIVE_CHECKOUT"),
          {
            type,
            trialPeriodDays,
            ...(attribution && { attribution }),
            ...(source && { conversion: { surface: source } }),
          },
          { withCredentials: true }
        );

        const { clientSecret } = response.data;

        if (!clientSecret && !trialPeriodDays) {
          throw new Error("Failed on starting checkout session.");
        }

        customEvent({
          name: `simple_payment_successful`,
          category: "Upgrade",
          label: "Successful Simple Payment",
          data: {
            user_id: user._id,
            user_email: user.email,
            user_handle: user.handle ?? "no_handle",
            trial_period: trialPeriodDays > 0,
          },
        });

        // Optimistic from here. The BE already created (or reactivated, or
        // synced from cache) the subscription record server-side, so the
        // user model already reflects `consumerSubscription`. We fire
        // confirmCardPayment + refetchAuth in the background — if either
        // errors after the user has moved on, we log to Rollbar but don't
        // surface; Stripe's dunning path picks up genuinely-bad cards via
        // webhook later.
        if (clientSecret) {
          stripe
            .confirmCardPayment(clientSecret)
            .then(({ error: confirmError }) => {
              if (confirmError) {
                rollbar.error(
                  "SimplePayment: confirmCardPayment failed after optimistic success",
                  {
                    user_id: user._id,
                    user_email: user.email,
                    error: confirmError.message,
                    code: confirmError.code,
                  }
                );
              }
            })
            .catch((err) => {
              rollbar.error(
                "SimplePayment: confirmCardPayment threw after optimistic success",
                { user_id: user._id, error: err?.message }
              );
            });
        }

        pollForSubscription({
          source: "SimplePayment",
          user_id: user._id,
          user_email: user.email,
          user_handle: user.handle ?? "no_handle",
          stripeCustomerId: user.stripeCustomerId,
          payment_type: type,
          trial_period: trialPeriodDays > 0,
        }).catch(() => {
          /* already logged inside the hook */
        });

        onSuccess();
        return true;
      } catch (err) {
        // The BE returns `requiresPaymentMethod` (402) when the user's saved
        // card is known-bad (their subscription was cancelled by a failed
        // payment). The optimistic saved-wallet path is intentionally blocked;
        // route the user to PaymentForm to enter a fresh card instead.
        const requiresPaymentMethod =
          err.response?.data?.requiresPaymentMethod === true;
        // An unusable session says nothing about the card: keep the saved card.
        const sessionError = isSessionError(err);
        const errorMessage =
          backendErrorMessage(err) ||
          err.message ||
          "An error occurred";
        // await for async db save before going to upgrade consumer page
        if (!sessionError) await setSimplePaymentFailed(true);
        customEvent({
          name: `simple_payment_failure`,
          category: "Upgrade",
          label: "Failure Simple Payment",
          data: {
            user_id: user._id,
            user_email: user.email,
            user_handle: user.handle ?? "no_handle",
            trial_period: trialPeriodDays > 0,
            message: errorMessage,
          },
        });
        const upgradeFlow = getUpgradeFlow(featureKey);
        // Surface the BE's known-bad-card reason (or the expired session) on the
        // upgrade page so the user knows what to do next.
        const state = requiresPaymentMethod || sessionError
          ? { ...upgradeFlow.state, paymentErrorMessage: errorMessage }
          : upgradeFlow.state;
        navigate(upgradeFlow.route, { state });
        return false;
      } finally {
        paymentInProgressRef.current = false;
      }
    },
    [
      navigate,
      onSuccess,
      pollForSubscription,
      setSimplePaymentFailed,
      stripe,
      trialPeriodDays,
      type,
      user,
    ]
  );

  return {
    handleSimplePayment,
    paymentStates,
    setPaymentStates,
    setSimplePaymentFailed,
    isSimplePayment,
    shouldTrySimplePayment,
    dbLoading,
  };
};
